// ============================================
// ADAPTIVE GRID UTILITIES
// ============================================
// Logic Pro–style adaptive time grid that reveals finer subdivisions as the user zooms in.
// All time values are in beats (quarter notes).

// Standard subdivision levels ordered coarse → fine.
// Each entry: { divisionsPerBeat, label, shortLabel }
export const SUBDIVISION_LEVELS = [
    { divisionsPerBeat: 1, label: '1/4 Note', shortLabel: '1/4' },
    { divisionsPerBeat: 2, label: '1/8 Note', shortLabel: '1/8' },
    { divisionsPerBeat: 4, label: '1/16 Note', shortLabel: '1/16' },
    { divisionsPerBeat: 8, label: '1/32 Note', shortLabel: '1/32' },
    { divisionsPerBeat: 16, label: '1/64 Note', shortLabel: '1/64' },
    { divisionsPerBeat: 32, label: '1/128 Note', shortLabel: '1/128' },
];

// Triplet subdivision levels
export const TRIPLET_LEVELS = [
    { divisionsPerBeat: 3, label: '1/4 Triplet', shortLabel: '1/4T' },
    { divisionsPerBeat: 6, label: '1/8 Triplet', shortLabel: '1/8T' },
    { divisionsPerBeat: 12, label: '1/16 Triplet', shortLabel: '1/16T' },
    { divisionsPerBeat: 24, label: '1/32 Triplet', shortLabel: '1/32T' },
];

// Minimum pixel gap between subdivision lines before they are shown
const MIN_SUBDIVISION_PX = 6;

/**
 * Given pixelsPerBeat, compute which subdivision levels should be visible.
 * Returns an array of { divisionsPerBeat, pixelsPerDiv, opacity, isBeat, isBar } sorted coarse→fine.
 *
 * @param {number} pixelsPerBeat
 * @param {number} beatsPerBar   - from time signature numerator
 * @param {number} [baseDivision=4] - user's chosen base grid (divisionsPerBeat): 1=1/4, 2=1/8, 4=1/16, etc.
 * @returns {Array<{divisionsPerBeat:number, pxPerDiv:number, opacity:number, isBase:boolean}>}
 */
export function getVisibleSubdivisions(pixelsPerBeat, beatsPerBar = 4, baseDivision = 4) {
    const result = [];

    for (const level of SUBDIVISION_LEVELS) {
        const pxPerDiv = pixelsPerBeat / level.divisionsPerBeat;
        if (pxPerDiv < MIN_SUBDIVISION_PX) break;

        // Opacity ramps up as lines become more spread out
        const opacity = Math.min(1, Math.max(0.15, (pxPerDiv - MIN_SUBDIVISION_PX) / 30));
        const isBase = level.divisionsPerBeat === baseDivision;

        result.push({
            divisionsPerBeat: level.divisionsPerBeat,
            pxPerDiv,
            opacity,
            isBase,
            label: level.shortLabel,
        });
    }

    return result;
}

/**
 * Build CSS background-image layers for the grid lines (piano roll & track lanes).
 * Returns { backgroundImage, backgroundSize } strings ready for inline style.
 *
 * @param {number} pixelsPerBeat
 * @param {number} beatsPerBar
 * @param {number} [baseDivision=4]
 */
export function buildGridCSS(pixelsPerBeat, beatsPerBar = 4, baseDivision = 4) {
    const barPx = pixelsPerBeat * beatsPerBar;
    const layers = [];
    const sizes = [];

    const subdivisions = getVisibleSubdivisions(pixelsPerBeat, beatsPerBar, baseDivision);

    // Finest to coarsest so that coarser lines paint over finer ones
    const reversed = [...subdivisions].reverse();

    for (const sub of reversed) {
        const px = sub.pxPerDiv;
        const alpha = sub.isBase
            ? Math.min(1, sub.opacity + 0.15)
            : sub.opacity * 0.6;
        const color = `rgba(255,255,255,${alpha.toFixed(3)})`;

        layers.push(
            `repeating-linear-gradient(to right, transparent 0px, transparent ${px - 1}px, ${color} ${px - 1}px, ${color} ${px}px)`
        );
        sizes.push(`${px}px 100%`);
    }

    // Beat lines
    const beatAlpha = 0.2;
    layers.push(
        `repeating-linear-gradient(to right, transparent 0px, transparent ${pixelsPerBeat - 1}px, rgba(255,255,255,${beatAlpha}) ${pixelsPerBeat - 1}px, rgba(255,255,255,${beatAlpha}) ${pixelsPerBeat}px)`
    );
    sizes.push(`${pixelsPerBeat}px 100%`);

    // Bar lines (strongest)
    layers.push(
        `repeating-linear-gradient(to right, transparent 0px, transparent ${barPx - 1}px, rgba(255,255,255,0.35) ${barPx - 1}px, rgba(255,255,255,0.35) ${barPx}px)`
    );
    sizes.push(`${barPx}px 100%`);

    return {
        backgroundImage: layers.join(',\n'),
        backgroundSize: sizes.join(', '),
    };
}

/**
 * Compute the snap interval in beats for a given zoom level and user base division.
 * Used for snapping note placement and dragging.
 */
export function getSnapInterval(pixelsPerBeat, baseDivision = 4) {
    // Start from the user's chosen base division
    let divisionsPerBeat = baseDivision;
    let pxPerDiv = pixelsPerBeat / divisionsPerBeat;

    // If zoomed out far enough that the base division is too crowded, snap to coarser grid
    while (pxPerDiv < MIN_SUBDIVISION_PX && divisionsPerBeat > 1) {
        divisionsPerBeat = Math.max(1, Math.floor(divisionsPerBeat / 2));
        pxPerDiv = pixelsPerBeat / divisionsPerBeat;
    }

    return 1 / divisionsPerBeat; // in beats
}

// ── Time conversion utilities ──────────────────

/**
 * Convert beats to bar:beat:subdivision string.
 * @param {number} beats - absolute beat position
 * @param {number} beatsPerBar
 * @param {number} subdivisionsPerBeat - for display granularity
 * @returns {string} e.g. "3:2:04"
 */
export function beatsToBarBeatSub(beats, beatsPerBar = 4, subdivisionsPerBeat = 4) {
    if (!Number.isFinite(beats) || beats < 0) beats = 0;
    const totalSubs = Math.round(beats * subdivisionsPerBeat);
    const subsPerBar = beatsPerBar * subdivisionsPerBeat;

    const bar = Math.floor(totalSubs / subsPerBar) + 1;
    const remainder = totalSubs % subsPerBar;
    const beat = Math.floor(remainder / subdivisionsPerBeat) + 1;
    const sub = remainder % subdivisionsPerBeat;

    return `${bar}:${beat}:${String(sub).padStart(2, '0')}`;
}

/**
 * Convert bar:beat:sub string back to beats.
 */
export function barBeatSubToBeats(str, beatsPerBar = 4, subdivisionsPerBeat = 4) {
    const parts = str.split(':').map(Number);
    const bar = (parts[0] || 1) - 1;
    const beat = (parts[1] || 1) - 1;
    const sub = parts[2] || 0;
    return bar * beatsPerBar + beat + sub / subdivisionsPerBeat;
}

/**
 * Convert beats to seconds given a BPM.
 */
export function beatsToSeconds(beats, bpm = 120) {
    return (beats / bpm) * 60;
}

/**
 * Convert seconds to beats given a BPM.
 */
export function secondsToBeats(seconds, bpm = 120) {
    return (seconds * bpm) / 60;
}

/**
 * Get a human-readable division label for a given divisionsPerBeat value.
 */
export function getDivisionLabel(divisionsPerBeat) {
    const all = [...SUBDIVISION_LEVELS, ...TRIPLET_LEVELS];
    const found = all.find(l => l.divisionsPerBeat === divisionsPerBeat);
    return found ? found.label : `1/${divisionsPerBeat * 4}`;
}

// ── Grid division options for UI selectors ──────
// gridSize encodes step = 4/gridSize beats (e.g. 16 → 1/4 beat = 1/16 note; 12 → 1/3 beat = 1/8 triplet).
export const GRID_DIVISION_OPTIONS = [
    { label: '1/1 Note', divisionsPerBeat: 0.25, gridSize: 1 },
    { label: '1/2 Note', divisionsPerBeat: 0.5, gridSize: 2 },
    { label: '1/4 Note', divisionsPerBeat: 1, gridSize: 4 },
    { label: '1/8 Note', divisionsPerBeat: 2, gridSize: 8 },
    { label: '1/16 Note', divisionsPerBeat: 4, gridSize: 16 },
    { label: '1/32 Note', divisionsPerBeat: 8, gridSize: 32 },
    { label: '1/64 Note', divisionsPerBeat: 16, gridSize: 64 },
    { label: '1/4 Triplet', divisionsPerBeat: 1.5, gridSize: 6 },
    { label: '1/8 Triplet', divisionsPerBeat: 3, gridSize: 12 },
    { label: '1/16 Triplet', divisionsPerBeat: 6, gridSize: 24 },
    { label: '1/32 Triplet', divisionsPerBeat: 12, gridSize: 48 },
];

/** Human label for the snap line (toolbar / status). */
export function getGridDivisionLabel(gridSize) {
    const n = Number(gridSize);
    const opt = GRID_DIVISION_OPTIONS.find(o => o.gridSize === n);
    return opt ? opt.label : `grid ${n}`;
}
